import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getHasDocumentCitations } from '@aline/common';
import { Typography } from '@mui/material';
import { AIReasoningIcon } from 'src/components/icons/AIReasoningIcon';
import { makeStyles } from 'tss-react/mui';

import type { CitationCardData } from './governing-document-sources';
import { RelationshipReasonMarkdown } from './RelationshipReasonMarkdown';
import { ShowMoreButton } from './ShowMoreButton';

/** Lines of reasoning shown before "Show more"; the rest expands in place, with no inner scrollbar. */
export const REASONING_VISIBLE_LINES = 5;

const useStyles = makeStyles()(theme => ({
  reasoningClamped: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: REASONING_VISIBLE_LINES,
    display: '-webkit-box',
    overflow: 'hidden',
  },
  reasoningColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  reasoningHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  reasoningMuted: {
    color: theme.palette.text.secondary,
  },
  reasoningSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  reasoningText: {
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
}));

interface ReasoningBodyProps {
  reasoning: string;
  /** Numbers each `#doc/` badge and backs its card. A citation missing from the map falls back to the CSS counter, with no card. */
  citationCardDataByDocumentId?: ReadonlyMap<string, CitationCardData>;
  /** Lands on the clamped body div, so it must not set `display` or `overflow`. */
  className?: string;
  /**
   * Applied to the rendered text on both the markdown and plain-text paths, so
   * a caller can override typography without touching the clamped wrapper.
   * Passing it switches the plain-text path to `variant="inherit"`, so the
   * className is the only source of typography rather than competing with
   * MUI's `body2` on specificity.
   */
  textClassName?: string;
}

/**
 * Just the reasoning content: markdown with citations when `reasoning` carries
 * a location link, plain text otherwise. No header or icon, so a caller
 * needing its own header chrome can supply it. Clamped to
 * `REASONING_VISIBLE_LINES` with a "Show more" toggle when longer.
 */
export function ReasoningBody({
  citationCardDataByDocumentId,
  className,
  reasoning,
  textClassName,
}: ReasoningBodyProps): JSX.Element {
  const { classes, cx } = useStyles();
  // Keyed by the text so a different reasoning starts collapsed again without an effect.
  const [expandedReasoning, setExpandedReasoning] = useState<string | null>(null);
  const expanded = expandedReasoning === reasoning;
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Shared by the initial measurement and the ResizeObserver below, so a
  // popover scrollbar appearing later (which narrows the body and re-wraps
  // the text) re-measures the same way as the first render did.
  const measureOverflow = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    // A hidden subtree reports 0 for both, and measuring it would wrongly
    // clear a real overflow.
    if (body.clientHeight === 0) return;
    setOverflows(body.scrollHeight > body.clientHeight);
  }, []);

  // `reasoning` and `citationCardDataByDocumentId` re-render the text, so they re-measure.
  useLayoutEffect(() => {
    if (expanded) return;
    measureOverflow();
  }, [citationCardDataByDocumentId, expanded, measureOverflow, reasoning]);

  useEffect(() => {
    if (expanded) return;
    const body = bodyRef.current;
    if (!body) return;
    const observer = new ResizeObserver(() => measureOverflow());
    observer.observe(body);
    return () => observer.disconnect();
  }, [expanded, measureOverflow]);

  const handleToggle = useCallback(() => {
    setExpandedReasoning(current => (current === reasoning ? null : reasoning));
  }, [reasoning]);

  return (
    <div className={classes.reasoningColumn}>
      <div ref={bodyRef} className={cx(!expanded && classes.reasoningClamped, className)}>
        {getHasDocumentCitations(reasoning) ? (
          <RelationshipReasonMarkdown
            citationCardDataByDocumentId={citationCardDataByDocumentId}
            className={textClassName}
            llmReasonsMarkdown={reasoning}
          />
        ) : (
          <Typography
            className={cx(classes.reasoningText, textClassName)}
            variant={textClassName ? 'inherit' : 'body2'}
          >
            {reasoning}
          </Typography>
        )}
      </div>
      {(overflows || expanded) && <ShowMoreButton expanded={expanded} onToggle={handleToggle} />}
    </div>
  );
}

interface AiReasoningSectionProps {
  reasoning: string | null | undefined;
  /** Per-document card data keyed by documentId, passed through to `ReasoningBody`. */
  citationCardDataByDocumentId?: ReadonlyMap<string, CitationCardData>;
  className?: string;
}

export function AiReasoningSection({
  citationCardDataByDocumentId,
  className,
  reasoning,
}: AiReasoningSectionProps) {
  const { classes, cx } = useStyles();

  if (!reasoning) return null;

  return (
    <div className={cx(classes.reasoningSection, className)}>
      <div className={classes.reasoningHeader}>
        <AIReasoningIcon height={14} width={14} />
        <Typography variant="caption">AI Reasoning</Typography>
      </div>
      <ReasoningBody
        citationCardDataByDocumentId={citationCardDataByDocumentId}
        reasoning={reasoning}
      />
    </div>
  );
}
